import { EventTypes } from '@candulabs/craft';
import { ButtonIcon, DuplicateIcon, Tooltip } from 'canoe';
import throttle from 'lodash/throttle';
import { highlight, languages } from 'prismjs/components/prism-core';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import CodeEditor from 'react-simple-code-editor';
import styled from 'styled-components';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-scala';
import 'prismjs/components/prism-bash';
import 'prismjs/themes/prism-funky.css';
// TODO(mat) figure out why this breakes
// import 'prismjs/components/prism-jsx';
// import 'prismjs/components/prism-tsx';
import { colors } from 'styleguide';

import Node from 'craft/components/Node';
import { NO_ACTION } from 'craft/constants';
import { CollectionContext } from 'craft/contexts';
import { useEditorNode, useEditorSelector, useNodeContext } from 'craft/hooks';
import { Resolver } from 'craft/types';

interface Props {
  childrenString?: string;
  language?: string;
}

const StyledNode = styled(Node)`
  z-index: 0; // relative z-index for codeeditor
`;

export const StyledCodeEditor = styled(CodeEditor)`
  font-size: 12px;
  font-family: 'Fira code', 'Fira Mono', monospace;
  color: ${colors('white')};
  border-radius: 4px;

  & > textarea {
    outline: none;

    &:disabled {
      cursor: initial;
      pointer-events: none;
    }
  }
`;

const StyledButtonIcon = styled(ButtonIcon)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
`;

const getLanguageSyntax = (language: string | undefined) => {
  switch (language) {
    case 'javascript': {
      return languages.js;
    }
    case 'css': {
      return languages.css;
    }
    case 'markup': {
      return languages.markup;
    }
    case 'xml': {
      return languages.markup;
    }
    case 'html': {
      return languages.markup;
    }
    case 'cpp': {
      return languages.clike;
    }
    case 'java': {
      return languages.java;
    }
    case 'scala': {
      return languages.scala;
    }
    case 'shell': {
      return languages.bash;
    }
    // TODO(mat) for some reason these dont import properly from prismjs
    // therefore will be highlighted with js default syntax only
    // case 'jsx': {
    //   return languages.jsx;
    // }
    // case 'tsx': {
    //   return languages.tsx;
    // }
    default:
      return languages.js;
  }
};

/**
 * Text stores the text both as a prop and as a state so that if we move the cursor
 * before we call `setProps` we still wont lose the state of the text.
 */
export const Code: Resolver<Props> = ({ childrenString, language }: Props) => {
  const { id } = useNodeContext();
  const ref = useRef<any>(null);
  const { setProps } = useEditorNode(id);

  const { id: collectionItemId } = useContext(CollectionContext);

  const [focusedRichText]: [string] = useEditorSelector((_state, q) => {
    const focusedElements = q.getEvent(EventTypes.focused);
    return focusedElements.all();
  });
  const isNodeInFocus = useMemo(() => {
    return focusedRichText === `${id}${collectionItemId ? `-${collectionItemId}` : ``}`;
  }, [focusedRichText, id, collectionItemId]);

  useEffect(() => {
    if (isNodeInFocus) {
      // eslint-disable-next-line no-underscore-dangle
      const dom = ref.current._input as HTMLInputElement;
      dom.focus();
      dom.setSelectionRange(dom.value.length, dom.value.length);
    }
  }, [isNodeInFocus]);

  // throttle a bit to reduce the number of calls
  const throttleSetText = throttle((value) => setProps({ childrenString: value }), 50);
  const onChange = (value: string) => {
    throttleSetText(value);
  };
  return (
    /**
     * hack: We remove spacing from the code editor so that all spacing will be applied
     * to the outer element.
     */
    <StyledNode
      data-atomictext-node
      data-code-editor={isNodeInFocus}
      css={{ backgroundColor: colors('neutral', -1), borderRadius: '4px', position: 'relative' }}
    >
      <Tooltip content="Users can click here to copy the code" placement="right" offset="s">
        <StyledButtonIcon
          m="xxs"
          radius="xsmall"
          size="small"
          icon={DuplicateIcon}
          color="contrast"
        />
      </Tooltip>
      <StyledCodeEditor
        ref={ref}
        disabled={!isNodeInFocus}
        style={{ backgroundColor: 'transparent', paddingRight: '25px' }}
        data-focus-listener={NO_ACTION}
        value={childrenString || ''}
        onValueChange={onChange}
        highlight={(code: string) => (code ? highlight(code, getLanguageSyntax(language)) : '')}
      />
    </StyledNode>
  );
};

Code.craft = {
  name: 'Code',
  defaultProps: {
    language: 'javascript',
  },
};
